import { useState, useEffect, useLayoutEffect, useCallback } from "react";
import { format, parse, isBefore, isAfter, isEqual, addHours, differenceInMilliseconds, addDays } from "date-fns";  // Import necessary date-fns functions
import { CheckCircleIcon, ClockIcon, ExclamationCircleIcon, ChevronLeftIcon, ChevronRightIcon, PlusCircleIcon, ArrowsRightLeftIcon, MagnifyingGlassPlusIcon, MagnifyingGlassMinusIcon, ClipboardDocumentCheckIcon, ChevronDoubleDownIcon, QuestionMarkCircleIcon, UserCircleIcon, Square2StackIcon, CakeIcon, ChatBubbleBottomCenterIcon, PaperAirplaneIcon, CubeIcon } from "@heroicons/react/20/solid";
import { useNavigate } from 'react-router-dom';
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend } from "recharts";
import { ArrowLeftStartOnRectangleIcon, MinusCircleIcon, XCircleIcon } from "@heroicons/react/24/solid";
import { useContext } from "react";
import { AuthContext } from "../AuthContext";
import ManagerNav from "./managernav";
import TaskHome from "./taskhome";
import Bottlenecks from "./bottlenecks";
import SubstepViewer from "./viewsubsteps";
import { UserAddIcon } from "@heroicons/react/20/solid";
import { ChevronDoubleRightIcon } from "@heroicons/react/20/solid";
import MessageAlert from "./messagealert";
import TaskList from "./projectdrag";

// RearrangeTasksDropdown Component
const RearrangeTasksDropdown = ({ groupedTasks, onTriggerParentEffect, onClose }) => {
  const [selectedTask, setSelectedTask] = useState(null);
  const [newTime, setNewTime] = useState("");
  const [conflictTask, setConflictTask] = useState(null);
  const [showConflictModal, setShowConflictModal] = useState(false);

  // Generate time slots from 06:00 to 23:00
  const generateTimeSlots = () => {
    const slots = [];
    for (let hour = 6; hour <= 23; hour++) {
      const timeStr = `${hour.toString().padStart(2, '0')}:00`;
      slots.push(timeStr);
    }
    return slots;
  };

  const timeSlots = generateTimeSlots();

  // Check for conflicts at the selected time
  const checkForConflict = (newTime, taskToMove) => {
    const tasksAtNewTime = groupedTasks[newTime] || [];
    // Find if there's a task with the same staff member at the new time
    const conflict = tasksAtNewTime.find(task => 
      task.Name === taskToMove.Name && task.TaskID !== taskToMove.TaskID
    );
    return conflict;
  };

  // Update task time in the backend
  const updateTaskTime = async (taskId, newStartTime) => {
    try {
      const formattedStartTime = `${newStartTime}:00`;
      const response = await fetch(`https://api.insightos.co.uk/api/updatetasktime/${taskId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          startTime: formattedStartTime,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Task updated successfully:', data);
        return true;
      } else {
        console.error('Failed to update task');
        const errorData = await response.json();
        console.error('Error details:', errorData);
        return false;
      }
    } catch (error) {
      console.error('Error updating task:', error);
      return false;
    }
  };

  // Handle time change for a task
  const handleTimeChange = async (task, newTime) => {
    if (!newTime) return;
    
    const conflict = checkForConflict(newTime, task);
    
    if (conflict) {
      setSelectedTask(task);
      setNewTime(newTime);
      setConflictTask(conflict);
      setShowConflictModal(true);
    } else {
      // No conflict, update directly
      const success = await updateTaskTime(task.TaskID, newTime);
      if (success) {
        onTriggerParentEffect(); // Refresh the data
        alert(`Task "${task.Task}" moved to ${newTime} successfully!`);
      } else {
        alert(`Failed to move task "${task.Task}". Please try again.`);
      }
    }
  };

  // Handle conflict resolution
  const handleConflictResolution = async (moveConflictingTask) => {
    try {
      if (moveConflictingTask) {
        // Move the conflicting task to a suggested time (next available slot)
        const suggestedTime = findNextAvailableSlot(conflictTask);
        if (suggestedTime) {
          const success1 = await updateTaskTime(conflictTask.TaskID, suggestedTime);
          const success2 = await updateTaskTime(selectedTask.TaskID, newTime);
          
          if (success1 && success2) {
            onTriggerParentEffect(); // Refresh the data
            alert(`Conflicting task moved to ${suggestedTime}, and "${selectedTask.Task}" moved to ${newTime}`);
          } else {
            alert("Failed to update one or both tasks. Please try again.");
          }
        } else {
          alert("No available time slot found for the conflicting task.");
        }
      } else {
        // Just move the selected task to the new time, keeping the conflict
        const success = await updateTaskTime(selectedTask.TaskID, newTime);
        if (success) {
          onTriggerParentEffect(); // Refresh the data
          alert(`Task "${selectedTask.Task}" moved to ${newTime}. Note: There may be scheduling conflicts.`);
        } else {
          alert("Failed to update task. Please try again.");
        }
      }
    } catch (error) {
      console.error('Error in conflict resolution:', error);
      alert("An error occurred. Please try again.");
    }
    
    setShowConflictModal(false);
    setSelectedTask(null);
    setNewTime("");
    setConflictTask(null);
  };

  // Find next available time slot for a staff member
  const findNextAvailableSlot = (task) => {
    for (const slot of timeSlots) {
      const tasksAtSlot = groupedTasks[slot] || [];
      const hasConflict = tasksAtSlot.some(t => t.Name === task.Name);
      if (!hasConflict) {
        return slot;
      }
    }
    return null;
  };

  return (
    <div className="space-y-4">
      <div className="text-sm text-indigo-900 mb-4">
        Select a new time for each task. If there's a conflict with the same staff member, you'll be prompted to resolve it.
      </div>
      
      {Object.entries(groupedTasks).map(([currentTime, tasks]) => (
        <div key={currentTime} className="rounded-2xl border border-indigo-200/70 bg-white/75 backdrop-blur-xl p-4 shadow-[0_12px_30px_rgba(99,102,241,0.16)]">
          <h4 className="font-semibold text-indigo-900 mb-3">Tasks at {currentTime}</h4>
          <div className="space-y-3">
            {tasks.map((task) => (
              <div key={task.TaskID} className="flex items-center justify-between bg-white/85 border border-indigo-200/70 p-3 rounded-xl shadow-sm">
                <div className="flex-1">
                  <div className="font-medium text-indigo-950">{task.Task}</div>
                  <div className="text-sm text-indigo-800">{task.Name} - {task.Role}</div>
                  <div className="text-xs text-indigo-500">{task.ProcessName}</div>
                </div>
                
                <div className="flex items-center space-x-3">
                  <div className="text-sm text-indigo-700">Move to:</div>
                  <select
                    className="rounded-xl border border-indigo-200/70 bg-white/85 px-3 py-1 text-sm text-indigo-900 shadow-sm focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400"
                    value=""
                    onChange={(e) => handleTimeChange(task, e.target.value)}
                    data-component="project-daily-tasks-rearrange"
                    data-feature="select_new_task_time"
                    data-task-id={task.TaskID}
                  >
                    <option value="">Select new time</option>
                    {timeSlots.map((slot) => (
                      <option key={slot} value={slot} disabled={slot === currentTime}>
                        {slot} {slot === currentTime ? "(current)" : ""}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Action Buttons */}
      <div className="flex justify-center pt-4 border-t border-indigo-200/60">
        <button
          onClick={onClose}
          className="px-6 py-2 rounded-xl border border-white/20 bg-gradient-to-br from-indigo-900/90 to-indigo-700/90 text-white shadow-lg shadow-indigo-950/35 hover:from-indigo-800/95 hover:to-indigo-600/95"
          data-component="project-daily-tasks-rearrange"
          

          data-feature="close_rearrange_panel"
        >
          Done Rearranging
        </button>
      </div>

      {/* Conflict Resolution Modal */}
      {showConflictModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-60">
          <div className="bg-white/95 border border-indigo-200/70 rounded-2xl shadow-[0_20px_45px_rgba(30,41,59,0.35)] max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-indigo-950 mb-4">Scheduling Conflict Detected</h3>
            
            <div className="space-y-3 mb-6">
              <p className="text-sm text-indigo-800">
                <strong>{conflictTask?.Name}</strong> already has a task at <strong>{newTime}</strong>:
              </p>
              <div className="bg-rose-50 border border-rose-200 rounded-xl p-3">
                <div className="font-medium text-red-800">{conflictTask?.Task}</div>
                <div className="text-sm text-red-600">{conflictTask?.ProcessName}</div>
              </div>
              
              <p className="text-sm text-indigo-800">
                You want to move <strong>"{selectedTask?.Task}"</strong> to this time.
              </p>
            </div>

            <div className="space-y-3">
              <button
                onClick={() => handleConflictResolution(true)}
                className="w-full px-4 py-2 rounded-xl border border-white/20 bg-gradient-to-br from-indigo-900/90 to-indigo-700/90 text-white shadow-md hover:from-indigo-800/95 hover:to-indigo-600/95"
                data-component="project-daily-tasks-rearrange"
                data-feature="resolve_conflict_move_put"
                data-task-id={selectedTask?.TaskID}
              >
                Move conflicting task to next available slot
              </button>
              
              <button
                onClick={() => handleConflictResolution(false)}
                className="w-full px-4 py-2 rounded-xl border border-white/20 bg-gradient-to-br from-amber-600/90 to-orange-500/90 text-white shadow-md hover:from-amber-500/95 hover:to-orange-400/95"
                data-component="project-daily-tasks-rearrange"
                data-feature="resolve_conflict_override_put"
                data-task-id={selectedTask?.TaskID}
              >
                Create conflict (both tasks at same time)
              </button>
              
              <button
                onClick={() => setShowConflictModal(false)}
                className="w-full px-4 py-2 rounded-xl border border-white/20 bg-gradient-to-br from-rose-600/90 to-red-500/90 text-white shadow-md hover:from-rose-500/95 hover:to-red-400/95"
                data-component="project-daily-tasks-rearrange"
                data-feature="resolve_conflict_cancel"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};



function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export default function DailyTaskTimeline({date, ProjectId, ProjectName, onDataChange }) {
  const user = useContext(AuthContext);

  const [tasks, setTasks] = useState([]);
  const [currentTask, setCurrentTask] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [timeToNextTask, setTimeToNextTask] = useState(0);  // Time to next task in milliseconds
  const [elapsedTime, setElapsedTime] = useState(0);  // Time elapsed since last update
  const [progress, setProgress] = useState(0);  // Progress bar value
  const [currentDate, setCurrentDate] = useState(new Date()); // State to manage current date
  const [instructionsTaskId, setInstructionsTaskId] = useState(null);
const [instructionText, setInstructionText] = useState("");
  const [taskTally, setTaskTally] = useState({});
  const [staffMembers, setStaffMembers] = useState({});
  const Navigate = useNavigate();
  const [changeTask, setChangeTask] = useState(null);
  const [change, setChange] = useState([]);
const [openDropdown, setOpenDropdown] = useState(null);
const [roles, setRoles] = useState([]);
const [newRole, setNewRole] = useState("");
const [zoom, setZoom] = useState(1);
const [bottleneck, setBottleneck] = useState(null);
const [selectedTaskId, setSelectedTaskId] = useState(null);
const [openOutcome, setOpenOutcome] = useState(null);
const [openOutcomes, setOpenOutcomes] = useState(null);
const [selectedProcess, setSelectProcess] = useState(null);
const [selectedTask, setSelectedTask] = useState("");
const [newTaskTrue, setNewTaskTrue] = useState(false);
const [rearrange, setRearrange] = useState("");
const [time, setTime] = useState("");
const [error, setError] = useState(null);
const [sendMessage, setSendMessage] = useState(null);
const [taskMessages, setTaskMessages] = useState([]);
const [expandedCard, setExpandedCard] = useState(null);
const [showChangeTaskModal, setShowChangeTaskModal] = useState(false);
const [changeTaskId, setChangeTaskId] = useState(null);
const [arrivals, setArrivals] = useState([]);
const [showArrivalButton, setShowArrivalButton] = useState(false);
const [showDepartureButton, setShowDepartureButton] = useState(false);
const [hasOnSiteTask, setHasOnSiteTask] = useState(false);
const [projectOnSite, setProjectOnSite] = useState(0);

  // Material usage states
  const [showMaterialDropdown, setShowMaterialDropdown] = useState(null);
  const [selectedMaterialForUse, setSelectedMaterialForUse] = useState(null);
  const [materialQuantity, setMaterialQuantity] = useState(1);
  const [projectMaterials, setProjectMaterials] = useState([]);
  const [projectType, setProjectType] = useState(null);

  // Break-related states
  const [showBreakForm, setShowBreakForm] = useState(false);
  const [breakStartTime, setBreakStartTime] = useState("");
  const [breakEndTime, setBreakEndTime] = useState("");
  const [selectedBreakTask, setSelectedBreakTask] = useState("");
  const [selectedBreakStaff, setSelectedBreakStaff] = useState("");
  const [breakLength, setBreakLength] = useState(null);
  const [availableTasks, setAvailableTasks] = useState([]);
  const [staff, setStaff] = useState([]);

  // Fetch tasks from the backend with the selected date

  console.log(ProjectName);

  console.log(selectedProcess);

  const handleTaskSelect = (taskId) => {
    setSelectedTaskId(taskId === selectedTaskId ? null : taskId); // Toggle selection
  };


  console.log(staffMembers)

  const userId = user.id

  console.log(selectedTask);
  // if date comes from the parent component, set the current date to the date from the parent component

  useEffect(() => {
    if (date) {
      setCurrentDate(date);
    }
  }, [date]);


  const fetchData = useCallback(() => {
    const fetchTasks = async () => {
      const token = localStorage.getItem('token');
      
      // First fetch project details to get materials and type
      try {
        const projectResponse = await fetch(`https://api.insightos.co.uk/api/getprojectsteps/${ProjectId}`, {
          method: "GET",
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        });

        if (projectResponse.ok) {
          const projectData = await projectResponse.json();
          if (projectData.success && projectData.project) {
            setProjectType(projectData.project.ProjectType);
            const onSiteValue = Number(projectData.project.OnSite) || 0;
            setProjectOnSite(onSiteValue);
            console.log('Project OnSite value:', projectData.project.OnSite, 'converted:', onSiteValue);
            
            // Handle arrivals
            if (projectData.project.Arrived) {
              try {
                const arrivalsData = typeof projectData.project.Arrived === 'string' 
                  ? JSON.parse(projectData.project.Arrived) 
                  : projectData.project.Arrived;
                setArrivals(Array.isArray(arrivalsData) ? arrivalsData : []);
              } catch (e) {
                console.error('Error parsing arrivals:', e);
                setArrivals([]);
              }
            } else {
              setArrivals([]);
            }
            
            if (projectData.project.Materials) {
              try {
                const materials = typeof projectData.project.Materials === 'string' 
                  ? JSON.parse(projectData.project.Materials) 
                  : projectData.project.Materials;
                setProjectMaterials(Array.isArray(materials) ? materials : []);
              } catch (e) {
                console.error('Error parsing materials:', e);
                setProjectMaterials([]);
              }
            }
          }
        }
      } catch (error) {
        console.error('Error fetching project details:', error);
      }

      // Then fetch tasks
      try {
        const response = await fetch("https://api.insightos.co.uk/api/projectdailytasks", {
          method: "POST",
          headers: { 'Content-Type': 'application/json', }, credentials: 'include',
          body: JSON.stringify({ date: format(currentDate, "yyyy-MM-dd"), ProjectId }),
        });




       

        if (!response.ok) {
          throw new Error("Failed to fetch tasks.");
        }

        const data = await response.json();

        // Sort tasks by StartTime (chronological order)
        const sortedTasks = data.data.sort((a, b) =>
          parse(a.StartTime, "HH:mm:ss", new Date()) - parse(b.StartTime, "HH:mm:ss", new Date())
        );

        setTasks(sortedTasks);
        
        // Check if user has any tasks today that match current date
        const todayDate = format(currentDate, 'yyyy-MM-dd');
        console.log('Today date:', todayDate, 'User ID:', userId);
        console.log('All tasks:', sortedTasks.map(t => ({ 
          TaskDate: t.TaskDate, 
          StaffId: t.StaffId, 
          Name: t.Name,
          Task: t.Task 
        })));
        
        // Use filter to see which tasks match
        const userTasksToday = sortedTasks.filter(task => {
          const taskDateFormatted = format(new Date(task.TaskDate), 'yyyy-MM-dd');
          const dateMatch = taskDateFormatted === todayDate;
          const staffMatch = Number(task.StaffId) === Number(userId);
          console.log('Task check:', { 
            Task: task.Task,
            TaskDate: task.TaskDate,
            taskDateFormatted,
            todayDate,
            dateMatch, 
            StaffId: task.StaffId,
            userId: userId,
            staffMatch 
          });
          return dateMatch && staffMatch;
        });
        
        console.log('Filtered user tasks today:', userTasksToday);
        const userHasTasksToday = userTasksToday.length > 0;
        console.log('userHasTasksToday:', userHasTasksToday);
        
        setHasOnSiteTask(userHasTasksToday);
        
        // Check if user has already arrived today
        const userArrivedToday = arrivals.some(arrival => 
          arrival.date === todayDate && Number(arrival.staffId) === Number(userId)
        );
        
        // Get onSiteValue from the closure (set earlier in fetchData)
        let currentOnSiteValue = projectOnSite;
        try {
          const projectResponse2 = await fetch(`https://api.insightos.co.uk/api/getprojectsteps/${ProjectId}`, {
            method: "GET",
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
          });
          if (projectResponse2.ok) {
            const projectData2 = await projectResponse2.json();
            if (projectData2.success && projectData2.project) {
              currentOnSiteValue = Number(projectData2.project.OnSite) || 0;
            }
          }
        } catch (e) {
          console.error('Error re-fetching OnSite:', e);
        }
        
        console.log('Arrival check:', { currentOnSiteValue, userHasTasksToday, userArrivedToday });
        
        // Show arrival button if: project is OnSite AND user has tasks today AND hasn't arrived yet
        setShowArrivalButton(currentOnSiteValue === 1 && userHasTasksToday && !userArrivedToday);

        // create a tally of the tasks for the day

        const taskTally = sortedTasks.reduce((tally, task) => {
          if (tally[task.Task]) {
            tally[task.Task] += 1;
          } else {
            tally[task.Task] = 1;
          }
          return tally;
        }, {});

        console.log(taskTally);

        // get the ProjectName from the first task in the sortedTasks array



        // now using.Name find out how different names are there in the taskTally object

        const staffMembers = sortedTasks.reduce((tally, task) => {
          if (tally[task.Name]) {

          } else {

            tally[task.Name] = 1;

          }

          return tally;

        }, {});

        // find out how many staff members are there in the staffMembers object

        setStaffMembers(staffMembers);

         // Get all the .Role from the sortedTasks array, remove duplicates, and then set them in state
setRoles([...new Set(sortedTasks.map((task) => task.Role))]);

          

        console.log(staffMembers);



        setTaskTally(taskTally);



        // Determine the current task based on the current time
        const now = new Date();
        const current = sortedTasks.find((task) => {
          const taskStartTime = parse(task.StartTime, "HH:mm:ss", new Date());
          const taskEndTime = addHours(taskStartTime, 1);  // Add 1 hour to simulate the task's end time
          return isEqual(taskStartTime, now) || (isBefore(taskStartTime, now) && isAfter(taskEndTime, now));
        });

        if (current) {
          setCurrentTask(current);
        }

        // Find the next task and calculate time to it
        const nextTask = sortedTasks.find((task) => {
          const taskStartTime = parse(task.StartTime, "HH:mm:ss", new Date());
          return isAfter(taskStartTime, now);
        });

        if (nextTask) {
          const nextTaskStartTime = parse(nextTask.StartTime, "HH:mm:ss", new Date());
          const timeDiff = differenceInMilliseconds(nextTaskStartTime, now);
          setTimeToNextTask(timeDiff);
        }
      } catch (error) {
        console.error("Error fetching tasks:", error);
      }
    }

    fetchTasks();

  }, [ProjectId, currentDate, userId, arrivals]);



  useEffect(() => {
    fetchData();
  }, [currentDate]);
  
  // Update arrival and departure button visibility
  useEffect(() => {
    const todayDate = format(currentDate, 'yyyy-MM-dd');
    const userArrival = arrivals?.find(arrival => 
      arrival.date === todayDate && String(arrival.staffId) === String(userId)
    );
    
    const userArrivedToday = !!userArrival;
    const userDepartedToday = userArrival?.departed ? true : false;
    
    console.log('useEffect arrival/departure check:', { 
      projectOnSite, 
      hasOnSiteTask, 
      userArrivedToday, 
      userDepartedToday,
      userId,
      userArrival
    });
    
    // Show arrival button if: on-site project, has on-site task, and hasn't arrived yet
    setShowArrivalButton(projectOnSite === 1 && hasOnSiteTask && !userArrivedToday);
    
    // Show departure button if: on-site project, has arrived, and hasn't departed
    setShowDepartureButton(projectOnSite === 1 && userArrivedToday && !userDepartedToday);
  }, [arrivals, hasOnSiteTask, projectOnSite, currentDate, userId, tasks, currentTime]);

  // Fetch staff members
  useEffect(() => {
    const fetchStaff = async () => {
      try {
        const response = await fetch("https://api.insightos.co.uk/api/staff", {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: 'include',
        });
        
        if (!response.ok) {
          throw new Error("Failed to fetch staff.");
        }
        
        const data = await response.json();
        setStaff(data.data);
      } catch (error) {
        console.error("Error fetching staff:", error);
      }
    };

    fetchStaff();
  }, []);



  useEffect (() => {

    const fetchRoles = async () => {
      const token = localStorage.getItem('token');

      // using changeTask to get the tasks from the backend

      

      

      try {
        const response = await fetch(`https://api.insightos.co.uk/api/staffRole/${selectedTask.role}`, {
          method: "GET",
          headers: { 'Content-Type': 'application/json', }, credentials: 'include',
        });

        if (!response.ok) {
          throw new Error("Failed to fetch roles.");
        }

        const data = await response.json();

        setRoles(data.data);

      } catch (error) {

        console.error("Error fetching roles:", error);

      }

    }

    fetchRoles();

  }, [selectedTask.role]);


  useEffect(() => {

    const fetchMessages = async () => {
  
      try {
        const response = await fetch(`https://api.insightos.co.uk/api/taskmessages/${sendMessage}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: 'include',
        });
  
        if (!response.ok) {
          throw new Error("Failed to fetch messages.");
        }
  
        const data = await response.json();
  
        console.log(data.data);
        setTaskMessages(data.data);
  
      } catch (error) {
        console.error("Error fetching messages:", error);
      }
  
    };
  
    fetchMessages();
  
  }
  
  ,[sendMessage]); // Fetch messages when sendMessage changes
  
  
  
  
  
  
  
  
  
  
  
  const handleSendMessage = async (taskId) => {
  
    try {
  
      const response = await fetch("https://api.insightos.co.uk/api/sendtaskmessage", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: 'include',
        body: JSON.stringify({ TaskID:taskId, content:instructionText }),
      });
  
  
      if (!response.ok) {
        throw new Error("Failed to send message.");
      }
  
      const data = await response.json();
  
      if (data.success) {
        console.log("Message sent successfully.");
        setSendMessage(null);
      }
  
  
      
    } catch (error) {
      console.error("Error sending message:", error);
  
    }
  }

 

  // Update the current time every second
  useEffect(() => {
    const intervalId = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(intervalId);  // Cleanup on unmount
  }, []);

  // Update progress bar every second
  useEffect(() => {
    if (timeToNextTask > 0) {
      const intervalId = setInterval(() => {
        setElapsedTime((prevTime) => prevTime + 1000);  // Increase elapsed time by 1 second

        // Calculate the progress as a percentage of the total time
        const progressPercent = (elapsedTime / timeToNextTask) * 100;

        if (progressPercent < 100) {
          setProgress(progressPercent);
        } else {
          setProgress(100);
        }
      }, 1000);

      return () => clearInterval(intervalId); // Cleanup on unmount
    }
  }, [timeToNextTask, elapsedTime]);

  // Scroll to current task after tasks have been rendered
  useLayoutEffect(() => {
    if (currentTask && tasks.length > 0) {
      const currentTaskElement = document.getElementById(currentTask.TaskID); // Ensure we're getting the correct element
      if (currentTaskElement) {
        currentTaskElement.scrollIntoView({ behavior: 'smooth', block: 'center' }); // Scroll smoothly to the current task
      }
    }
  }, [currentTask, tasks]); // Re-run if currentTask or tasks change

  // Handle date change (Previous or Next)
  const handleDateChange = (direction) => {
    const newDate = addDays(currentDate, direction === 'next' ? 1 : -1);  // Add or subtract one day
    setCurrentDate(newDate);
  };

  const groupedTasks = tasks.reduce((groups, task) => {
    const taskStartTime = format(parse(task.StartTime, "HH:mm:ss", new Date()), "HH:mm");
    if (!groups[taskStartTime]) {
      groups[taskStartTime] = [];
    }
    groups[taskStartTime].push(task);
    return groups;
  }, {});

  // Prepare data for the Pie Chart
  const pieChartData = Object.entries(taskTally).map(([task, count]) => ({
    name: task,
    value: count,
  }));

  const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#AF19FF"];

  // Handle priority change

const handlePriorityChange = async (taskId, action) => {


  // Find the task by ID
  const priorityLevels = ["Low", "Medium", "High"]; // Priority levels in order

  // match the task id with the task id in the tasks array
  const task = tasks.find((task) => task.TaskID === taskId);

  // find the index of the current priority level
  const currentPriorityIndex = priorityLevels.indexOf(task.Priority);

  let newPriorityIndex = currentPriorityIndex;

  if (action === "increase" && currentPriorityIndex < priorityLevels.length - 1) {
    newPriorityIndex += 1; // Move up in priority
  } else if (action === "decrease" && currentPriorityIndex > 0) {
    newPriorityIndex -= 1; // Move down in priority
  }

  const newPriority = priorityLevels[newPriorityIndex];

  console.log("New Priority:", newPriority);

  
  const id = taskId;

  try {

  const response = await fetch("https://api.insightos.co.uk/api/prioritychange", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    credentials : 'include',

    body: JSON.stringify({ id, priority: newPriority }), // Change "newPriority" to "priority"
  });
  
  

    if (!response.ok) {
      throw new Error("Failed to update priority.");
    }

    const data = await response.json();

    if (data.success) {

   fetchData();
   
      console.log("Priority updated successfully.");
    }
  }

  catch (error) {
    console.error("Error updating priority:", error);
  }

};



useEffect(() => {

  // get the tasks from the backend
  async function fetchTasks() {
    

    try {
      const response = await fetch("https://api.insightos.co.uk/api/tasks", {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("Failed to fetch tasks.");
      }

      const data = await response.json();

      // filter Tasks from the data


      const tasks = data.data;

// Map over the data to extract the task strings, then split each string into individual tasks, and merge them into one array
const all = data.data
  .map((task) => task.Tasks) // Get the nested arrays of tasks
  .flat() // Flatten the array of arrays
  .map((task) => task.split(',')) // Split each task string by commas
  .flat(); // Flatten again to get one array of individual tasks

// Set the merged array to state
setChange(all);

      






     
    } catch (error) {
      console.error("Error fetching tasks:", error);
    }
  }

  fetchTasks();

}, [changeTask]); // Empty dependency array to run once



const handleNewTask = async () => {
  
 


  try {

    const response = await fetch("https://api.insightos.co.uk/api/newtaskproject", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify({ 
        Description: instructionText,
        Name : newRole.Name,
        StaffId : newRole.id,
        ProcessId : selectedProcess.id,
        ProcessName : selectedProcess.Name,
        Task : selectedTask.task,

        TaskId : selectedTask.id,
        ProjectId : ProjectId,
        StartTime : time,
        Role : selectedTask.role,
        TaskDate : format(currentDate, "yyyy-MM-dd"),
        ProjectName : ProjectName,
        Step : selectedTask.step,

      










       }),
    });

    if (!response.ok) {
      throw new Error("Failed to update task.");
    }

    const data = await response.json();

    if (data.success) {

      console.log("Task updated successfully.");

      setNewTaskTrue(false);

    

fetchData();

    }

  } catch (error) {
    console.error("Error updating task:", error);
  }

};



const handleSaveOutcome = async (taskId) => {

  const token = localStorage.getItem('token');

  try {

    const response = await fetch(`https://api.insightos.co.uk/api/updatedailytask/${taskId}`, {

      method: "PUT",
      headers: { 'Content-Type': 'application/json', }, credentials: 'include',
      body: JSON.stringify({ instructionText: instructionText }),
    });

    if (!response.ok) {
      throw new Error("Failed to update task.");
    }

    const data = await response.json();

    if (data.success) {
      console.log("Task updated successfully.");
      setOpenOutcome(null);
      setInstructionText("");
      fetchData();
    }

  } catch (error) {

    console.error("Error updating task:", error);

  }

};








  // Handle Zoom In
  const handleZoomIn = () => setZoom((prev) => prev + 0.1);
  const handleZoomOut = () => setZoom((prev) => (prev - 0.1 > 0.5 ? prev - 0.1 : 0.5));



// Function to calculate and return task completion percentages
const calculateTaskCompletion = () => {

  const tasksWithoutBreak = tasks.filter((task) => !task.Task.toLowerCase().includes("Break"));

  const completedTasks = tasksWithoutBreak.filter((task) => task.Completed === 1).length;
  
  const incompleteTasks = tasksWithoutBreak.filter((task) => task.Completed === 0).length;
  const totalTasks = tasksWithoutBreak.length;

  // Handle division by zero when there are no tasks
  const totalSafe = totalTasks || 1;
  const completedPercent = (completedTasks / totalSafe) * 100;
  const incompletePercent = (incompleteTasks / totalSafe) * 100;

  return { completedPercent, incompletePercent, completedTasks, incompleteTasks, totalTasks };
};



  const { completedPercent, incompletePercent, completedTasks, incompleteTasks, totalTasks } = calculateTaskCompletion();

  console.log(selectedTask)
  

const handleSaveTask = async (taskId) => {

    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`https://api.insightos.co.uk/api/editproject/${taskId}`, {
        method: "PUT",
        headers: { 'Content-Type': 'application/json', }, credentials: 'include',
        body: JSON.stringify({
          Description: instructionText,
          Name : newRole.Name,
          StaffId : newRole.id,
          ProcessId : selectedProcess.id,
          ProcessName : selectedProcess.Name,
          Task : selectedTask.task,
          TaskId : selectedTask.id,
          Step : selectedTask.step,
          



        }),
      });

      if (!response.ok) {
        throw new Error("Failed to complete task.");
      }

        const data = await response.json();

        console.log(data);

        if (data.success) {
          console.log("Task completed successfully.");
          setOpenOutcome(null);
            fetchData();
            setOpenDropdown(null);
            setSelectProcess(null)  
            setNewRole("");
            setSelectedTask("");
        }



    } catch (error) {
      console.error("Error completing task:", error);
    }
  };

  const handleGetNewTask = async () => {


      const token = localStorage.getItem('token');
  
      try {
        const response = await fetch(`https://api.insightos.co.uk/api/getprocesstasks`, {
          method: "GET",
          headers: { 'Content-Type': 'application/json', }, credentials: 'include',
        });
  
        if (!response.ok) {
          throw new Error("Failed to fetch tasks.");
        }
  
        const data = await response.json();
  
        console.log(data.data);
  
        if (data.success) {
          console.log("Tasks fetched successfully.");
          setChangeTask(data.data);
          setNewTaskTrue(true);
        }
  
  
      } catch (error) {
  
        console.error("Error fetching tasks:", error);
  
      }
  
    };



  const handleTasksChange = async (taskId) => {

    const token = localStorage.getItem('token');

    try {
      const response = await fetch(`https://api.insightos.co.uk/api/getprocesstasks`, {
        method: "GET",
        headers: { 'Content-Type': 'application/json', }, credentials: 'include',
      });

      if (!response.ok) {
        throw new Error("Failed to fetch tasks.");
      }

      const data = await response.json();

      console.log(data.data);

      if (data.success) {
        console.log("Tasks fetched successfully.");
        setChangeTask(data.data);
        setChangeTaskId(taskId);
        setShowChangeTaskModal(true);
      }


    } catch (error) {

      console.error("Error fetching tasks:", error);

    }

  };









  const handleCompleteTask = async (taskId) => {

    setOpenOutcome(taskId);

  
  };


  const duplicateTask = async (taskId) => {

    const token = localStorage.getItem('token');

    try {

      const response = await fetch(`https://api.insightos.co.uk/api/duplicatetask/${taskId}`, {
        method: "POST",
        headers: { 'Content-Type': 'application/json', }, credentials: 'include',
      });

      if (!response.ok) {
        throw new Error("Failed to duplicate task.");
      }

      const data = await response.json();

      if (data.success) {
        console.log("Task duplicated successfully.");
        fetchData();
      }

    } catch (error) {
      console.error("Error duplicating task:", error);
    }

  };

  const handleDeleteTask = async (taskId) => {

    const token = localStorage.getItem('token');

    try {

      const response = await fetch(`https://api.insightos.co.uk/api/deletetask/${taskId}`, {
        method: "DELETE",
        headers: { 'Content-Type': 'application/json', }, credentials: 'include',
      });

      if (!response.ok) {
        throw new Error("Failed to delete task.");
      }

      const data = await response.json();

      if (data.success) {
        console.log("Task deleted successfully.");
        fetchData();
      }

    } catch (error) {

      console.error("Error deleting task:", error);

    }

  };




  const handleSetTime = (selectedTime) => {
    // Check if there's a task at the selected time
    const taskAtTime = groupedTasks[selectedTime];
  
    if (taskAtTime) {
      // Check if any task at this time has the same staff member (newRole.Name)
      const isConflict = taskAtTime.some(task => task.Name === newRole.Name);
  
      if (isConflict) {
        console.log("🚫 Time already assigned to this staff member!");
        setError("Time already assigned to this staff member!");
        return; // Reject the time
      }
    }
  
    // ✅ Allow setting the time
    setTime(selectedTime);
    setError(null); // Clear any previous error
    console.log("✅ Time is available!");
  };
  
  // Fetch break length for selected staff member
  const fetchBreakLength = async (staffId) => {
    if (!staffId) return;
    
    try {
      const response = await fetch(`https://api.insightos.co.uk/api/taskcalendar/${staffId}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: 'include',
        body: JSON.stringify({
          dates: [format(currentDate, "yyyy-MM-dd")],
        }),
      });

      if (response.ok) {
        const data = await response.json();
        const breakLength = data.breaklength || 0;
        setBreakLength(breakLength);
        console.log("Break length for staff member:", breakLength);
      }
    } catch (error) {
      console.error("Error fetching break length:", error);
    }
  };

  // Fetch break length when staff member is selected
  useEffect(() => {
    if (selectedBreakStaff) {
      fetchBreakLength(selectedBreakStaff);
    }
  }, [selectedBreakStaff, currentDate]);

  // Handle break button click - show break form
  const handleBreakClick = () => {
    // Fetch available tasks for additional task selection
    fetchTasksForBreak();
    setShowBreakForm(true);
  };

  // Fetch tasks for break additional task selection
  const fetchTasksForBreak = async () => {
    try {
      const response = await fetch(`https://api.insightos.co.uk/api/tasks/${ProjectId}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        const tasks = data.data || [];
        
        // Filter out Training tasks and add proper structure
        const filteredTasks = tasks
          .filter(task => task.Task && task.Task !== "Training")
          .map(task => ({
            id: task.id,
            name: task.Task,
            step: task.Step,
            processName: task.ProcessName,
            processId: task.ProcessId,
            description: task.Description,
          }));

        setAvailableTasks(filteredTasks);
      }
    } catch (error) {
      console.error("Error fetching tasks for break:", error);
    }
  };

  // Handle break form submission
  const handleBreakSubmit = async () => {
    if (!selectedBreakStaff) {
      alert('Please select a staff member');
      return;
    }

    if (!breakStartTime || !breakEndTime) {
      alert('Please enter both start and end times for the break');
      return;
    }

    // Get selected staff member info
    const selectedStaff = staff.find(member => member.id === parseInt(selectedBreakStaff));
    if (!selectedStaff) {
      alert('Selected staff member not found');
      return;
    }



    // Calculate break duration in minutes
    const [startHour, startMinute] = breakStartTime.split(":").map(Number);
    const [endHour, endMinute] = breakEndTime.split(":").map(Number);
    const startDate = new Date(1970, 0, 1, startHour, startMinute);
    const endDate = new Date(1970, 0, 1, endHour, endMinute);
    let duration = Math.round((endDate - startDate) / (1000 * 60));
    if (duration < 0) duration += 1440; // handle overnight breaks

    // Build break task data in the same structure as other tasks
    const additionalTaskObj = selectedBreakTask
      ? availableTasks.find(task => task.id === parseInt(selectedBreakTask))
      : null;

    const taskData = {
      Date: format(currentDate, "yyyy-MM-dd"),
      StartTime: breakStartTime, // Default start time for breaks
      Task: additionalTaskObj ? additionalTaskObj.name : "Break",
      taskId: additionalTaskObj ? additionalTaskObj.id : null,
      ProjectName: ProjectName,
      ProcessName: additionalTaskObj ? additionalTaskObj.processName : null,
      ProcessId: additionalTaskObj ? additionalTaskObj.processId : null,
      Step: additionalTaskObj ? additionalTaskObj.step : null,
      Description: additionalTaskObj ? additionalTaskObj.description : `Break from ${breakStartTime} to ${breakEndTime}`,
      Role: additionalTaskObj ? additionalTaskObj.Role : null,
      isBreak: 1,
      breakStartTime,
      breakEndTime,
      breakDuration: duration,
      Name: selectedStaff.Name,
      StaffId: selectedStaff.id,
      ProjectId: ProjectId,
      Priority: "Medium",
      Status: "Not Started",
    };

    console.log(taskData)
   

    try {
      const response = await fetch("https://api.insightos.co.uk/api/savebreak", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: 'include',
        body: JSON.stringify(taskData),
      });

      if (response.ok) {
        console.log("Break added successfully");
        handleBreakCancel();
        fetchData(); // Refresh the data instead of full page reload
      } else {
        throw new Error("Failed to add break");
      }
    } catch (error) {
      console.error("Error adding break:", error);
      alert("Failed to add break. Please try again.");
    }
  };

  // Handle break form cancel
  const handleBreakCancel = () => {
    setShowBreakForm(false);
    setBreakStartTime("");
    setBreakEndTime("");
    setSelectedBreakTask("");
    setSelectedBreakStaff("");
    setBreakLength(null);
  };

  // Handle additional task selection
  const handleAdditionalTaskChange = (e) => {
    setSelectedBreakTask(e.target.value);
  };

  // Helper for geolocation (mobile-safe)
  const getGeoTag = () =>
    new Promise((resolve) => {
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      const timeout = isMobile || isIOS ? 5000 : 10000;
      
      if (window.electron && window.electron.GeoAPI) {
        window.electron.GeoAPI.getLocation()
          .then((geoTag) => resolve(geoTag))
          .catch(() => resolve('Unknown'));
      } else if (navigator.geolocation) {
        const options = {
          enableHighAccuracy: false,
          timeout: timeout,
          maximumAge: 300000
        };
        
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const { latitude, longitude } = position.coords;
            resolve(`${latitude},${longitude}`);
          },
          (error) => {
            console.log('Geolocation failed:', error);
            resolve('Unknown');
          },
          options
        );
      } else {
        resolve('Unknown');
      }
    });

  // Handle arrival button click
  const handleArrival = async () => {
    try {
      const geotag = await getGeoTag();
      const response = await fetch('https://api.insightos.co.uk/api/projectarrival', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ projectId: ProjectId, geotag })
      });

      const data = await response.json();
      if (data.success) {
        setArrivals(data.arrivals || []);
        setShowArrivalButton(false);
        if (onDataChange) onDataChange(); // Trigger parent refetch
        alert('Arrival recorded successfully!');
      } else {
        alert('Failed to record arrival: ' + (data.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error recording arrival:', error);
      alert('Error recording arrival');
    }
  };

  // Handle departure button click
  const handleDeparture = async () => {
    try {
      const geotag = await getGeoTag();
      const response = await fetch('https://api.insightos.co.uk/api/projectdeparture', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ projectId: ProjectId, geotag })
      });

      const data = await response.json();
      if (data.success) {
        setArrivals(data.arrivals || []);
        setShowDepartureButton(false);
        if (onDataChange) onDataChange(); // Trigger parent refetch
        alert('Departure recorded successfully!');
      } else {
        alert('Failed to record departure: ' + (data.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error recording departure:', error);
      alert('Error recording departure');
    }
  };

  // Handle material usage
  const handleUseMaterial = async (taskId, taskName, materialIndex, quantity) => {
    try {
      const response = await fetch(`https://api.insightos.co.uk/api/usedmaterial/${ProjectId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          materialIndex,
          usedQuantity: quantity,
          taskName: taskName
        }),
      });

      const result = await response.json();

      if (response.ok) {
        alert(`Material used successfully for ${taskName}`);
        setShowMaterialDropdown(null);
        setSelectedMaterialForUse(null);
        setMaterialQuantity(1);
        // Refresh data to get updated materials
        fetchData();
      } else {
        alert(`Failed to use material: ${result.message}`);
      }
    } catch (error) {
      console.error('Error using material:', error);
      alert('Error using material');
    }
  };

  // Get bought materials only
  const getBoughtMaterials = () => {
    return projectMaterials.filter(m => m.bought === true && m.quantity > 0);
  };

  
 
  








  return (
    <>
    <div className="fixed top-4 right-4 z-50">
      <MessageAlert />
    </div>

<header className="flex flex-col sm:flex-row items-center justify-between border-b border-indigo-200/60 bg-gradient-to-r from-indigo-100/80 via-white/85 to-purple-100/80 backdrop-blur-xl shadow-[0_12px_30px_rgba(99,102,241,0.2)] p-3 sm:p-6 gap-2 sm:gap-4">
  {/* Navigation Buttons */}
  <div className="flex flex-col sm:flex-row w-full gap-2 sm:gap-4">
    <button 
      onClick={handleGetNewTask} 
      className="flex items-center justify-center w-full sm:w-auto rounded-xl border border-white/20 bg-gradient-to-br from-indigo-900/90 to-indigo-700/90 text-white px-3 py-1.5 sm:px-4 sm:py-2 shadow-lg shadow-indigo-950/35 hover:from-indigo-800/95 hover:to-indigo-600/95 text-sm sm:text-base"
    >
      <PlusCircleIcon className="h-5 w-5 sm:h-6 sm:w-6 mr-1 sm:mr-2" aria-hidden="true" />
      <span>Add Task</span>
    </button>

    <div className="flex flex-row justify-between w-full sm:w-auto gap-2 sm:gap-4">
      <button 
        onClick={() => handleDateChange('prev')}
        className="flex items-center justify-center w-full sm:w-auto rounded-xl border border-white/20 bg-gradient-to-br from-rose-600/90 to-red-500/90 text-white px-3 py-1.5 sm:px-4 sm:py-2 shadow-lg shadow-rose-900/35 hover:from-rose-500/95 hover:to-red-400/95 text-sm sm:text-base"
      >
        <ChevronLeftIcon className="h-5 w-5 sm:h-6 sm:w-6 mr-1 sm:mr-2" aria-hidden="true" />
        <span>Previous</span>
      </button>

      <button 
        onClick={() => handleDateChange('next')}
        className="flex items-center justify-center w-full sm:w-auto rounded-xl border border-white/20 bg-gradient-to-br from-emerald-700/90 to-emerald-500/90 text-white px-3 py-1.5 sm:px-4 sm:py-2 shadow-lg shadow-emerald-900/35 hover:from-emerald-600/95 hover:to-emerald-400/95 text-sm sm:text-base"
      >
        <span className="mr-1 sm:mr-2">Next</span>
        <ChevronRightIcon className="h-5 w-5 sm:h-6 sm:w-6" aria-hidden="true" />
      </button>
    </div>
  </div>

  {/* Pie Chart & Legend */}
  {pieChartData.length > 0 && (
    <div className="flex flex-col sm:flex-row w-full items-center justify-between gap-2 sm:gap-4 mt-3 sm:mt-0">
      {/* Legend */}
      <div className="text-xs sm:text-sm font-semibold flex flex-wrap gap-1 sm:gap-2">
        {pieChartData.map((entry, index) => (
          <div key={index} className="flex items-center space-x-1">
            <span className="inline-block w-2 h-2 sm:w-3 sm:h-3 rounded-full" style={{ backgroundColor: COLORS[index % COLORS.length] }}></span>
            <span className="text-xs">{entry.name}</span>
          </div>
        ))}
      </div>

      {/* Pie Chart */}
      <div className="w-20 h-20 sm:w-24 sm:h-24 md:w-28 md:h-28 flex-shrink-0">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={pieChartData}
              cx="50%"
              cy="50%"
              outerRadius="75%"
              fill="#8884d8"
              dataKey="value"
            >
              {pieChartData.map((_, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  )}
</header>




{/* Sticky Header */}
<div className="h-full flex flex-col bg-gradient-to-b from-indigo-100 via-sky-50 to-emerald-50" data-component="project-daily-timeline">
  <header className="sticky top-0 z-10 bg-gradient-to-r from-indigo-100/80 via-white/85 to-purple-100/80 backdrop-blur-xl border-b border-indigo-200/60 shadow-[0_12px_30px_rgba(99,102,241,0.2)] p-3 sm:p-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
    <h1 className="text-lg sm:text-xl font-bold text-indigo-600 truncate">
      {format(currentDate, "EEE, MMM d, yyyy")}
    </h1>

    <div className="flex flex-wrap gap-2 items-center">
      <button 
        onClick={handleBreakClick} 
        className="flex items-center space-x-1 sm:space-x-2 rounded-xl border border-white/20 bg-gradient-to-br from-amber-600/90 to-orange-500/90 text-white px-2 py-1 sm:px-3 sm:py-2 text-xs sm:text-sm shadow-md hover:from-amber-500/95 hover:to-orange-400/95"
      >
        <CakeIcon className="h-4 w-4 sm:h-5 sm:w-5" aria-hidden="true" />
        <span>Add Break</span>
      </button>
      
      {/* Zoom controls in a row */}
      <div className="flex items-center space-x-1">
        <button
          onClick={handleZoomOut}
          className="rounded-xl border border-indigo-200/70 bg-white/75 p-1 sm:px-3 sm:py-2 text-indigo-900 shadow-sm hover:bg-white"
        >
          <MagnifyingGlassMinusIcon className="h-4 w-4 sm:h-5 sm:w-5" aria-hidden="true" />
        </button>
        <button
          onClick={handleZoomIn}
          className="rounded-xl border border-indigo-200/70 bg-white/75 p-1 sm:px-3 sm:py-2 text-indigo-900 shadow-sm hover:bg-white"
        >
          <MagnifyingGlassPlusIcon className="h-4 w-4 sm:h-5 sm:w-5" aria-hidden="true" />
        </button>
      </div>
      
      <div className="flex items-center p-1 sm:p-2 rounded-xl border border-white/20 bg-gradient-to-br from-indigo-900/90 to-indigo-700/90 shadow-md">
        <ClockIcon className="h-4 w-4 sm:h-5 sm:w-5 text-white" aria-hidden="true" />
        <span className="text-sm sm:text-lg text-white ml-1 font-bebas">
          {format(currentTime, "HH:mm")}
        </span>
      </div>
    </div>
  </header>
  
  {/* Arrivals Display */}
  {arrivals?.length > 0 && arrivals.filter(arrival => arrival.date === format(currentDate, 'yyyy-MM-dd')).length > 0 && (
    <div className="bg-white/80 border border-emerald-200/70 rounded-2xl shadow-sm p-4 mx-4 mt-4">
      <div className="flex items-start">
        <div className="flex-shrink-0">
          <CheckCircleIcon className="h-5 w-5 text-green-400" aria-hidden="true" />
        </div>
        <div className="ml-3 flex-1">
          <h3 className="text-sm font-medium text-emerald-800">Project Arrivals & Departures</h3>
          <div className="mt-2 text-sm text-emerald-700">
            <ul className="space-y-2">
              {arrivals
                .filter(arrival => arrival.date === format(currentDate, 'yyyy-MM-dd'))
                .map((arrival, index) => (
                  <li key={index} className="space-y-1">
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <span className="font-medium">{arrival.name}</span>
                      <span>-</span>
                      <span>Arrived: {arrival.arrivalTime}</span>
                    </div>
                    {arrival.departed && (
                      <div className="flex items-center space-x-2 ml-4 text-purple-700">
                        <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                        <span>Departed: {arrival.departed}</span>
                      </div>
                    )}
                  </li>
                ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  )}
  
  {/* Arrival Button */}
  {showArrivalButton && (
    <div className="bg-white/80 border border-sky-200/70 rounded-2xl shadow-sm p-4 mx-4 mt-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <ExclamationCircleIcon className="h-5 w-5 text-blue-400" aria-hidden="true" />
          </div>
          <div className="ml-3">
            <p className="text-sm text-sky-700">
              You have an on-site task today. Please confirm your arrival.
            </p>
          </div>
        </div>
        <button
          onClick={handleArrival}
          className="ml-4 inline-flex items-center rounded-xl border border-white/20 bg-gradient-to-br from-indigo-900/90 to-indigo-700/90 px-4 py-2 text-sm font-medium text-white shadow-md hover:from-indigo-800/95 hover:to-indigo-600/95"
        >
          Confirm Arrival
        </button>
      </div>
    </div>
  )}
  
  {/* Departure Button */}
  {showDepartureButton && (
    <div className="bg-white/80 border border-purple-200/70 rounded-2xl shadow-sm p-4 mx-4 mt-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <CheckCircleIcon className="h-5 w-5 text-purple-400" aria-hidden="true" />
          </div>
          <div className="ml-3">
            <p className="text-sm text-purple-700">
              If all tasks are completed. Please confirm your departure.
            </p>
          </div>
        </div>
        <button
          onClick={handleDeparture}
          className="ml-4 inline-flex items-center rounded-xl border border-white/20 bg-gradient-to-br from-purple-700/90 to-indigo-700/90 px-4 py-2 text-sm font-medium text-white shadow-md hover:from-purple-600/95 hover:to-indigo-600/95"
        >
          Confirm Departure
        </button>
      </div>
    </div>
  )}
  
  <div className="w-full bg-gradient-to-r from-indigo-100/80 via-white/85 to-purple-100/80 backdrop-blur-xl border-y border-indigo-200/60 p-2 sm:p-4">
<div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:space-x-4">
  {bottleneck ? (
    <button
      onClick={() => setBottleneck(null)}
      type="button"
      className="inline-flex items-center gap-x-1 rounded-xl border border-white/20 bg-gradient-to-br from-rose-600/90 to-red-500/90 px-2 py-1 sm:px-3.5 sm:py-2.5 text-xs sm:text-sm font-semibold text-white shadow-lg shadow-rose-900/35 hover:from-rose-500/95 hover:to-red-400/95"
    >
      <ArrowLeftStartOnRectangleIcon aria-hidden="true" className="-ml-0.5 size-4 sm:size-5" />
      Back
    </button>
  ) : (
      
<button onClick={() => setBottleneck(tasks)}
        type="button"
        className="inline-flex items-center gap-x-1 rounded-xl border border-white/20 bg-gradient-to-br from-indigo-900/90 to-indigo-700/90 px-2 py-1 sm:px-3.5 sm:py-2.5 text-xs sm:text-sm font-semibold text-white shadow-lg shadow-indigo-950/40 hover:from-indigo-800/95 hover:to-indigo-600/95"
      >
        <ChevronDoubleDownIcon aria-hidden="true" className="-ml-0.5 size-4 sm:size-5" />
       Bottlenecks
      </button>
  )}
  {rearrange ? (
    <button onClick={() => setRearrange(false)}
    type="button"
    className="inline-flex items-center gap-x-1 rounded-xl border border-white/20 bg-gradient-to-br from-rose-600/90 to-red-500/90 px-2 py-1 sm:px-3.5 sm:py-2.5 text-xs sm:text-sm font-semibold text-white shadow-lg shadow-rose-900/35 hover:from-rose-500/95 hover:to-red-400/95"
  >
    <XCircleIcon aria-hidden="true" className="-ml-0.5 size-4 sm:size-5" />
   Close
  </button>
  ) : (
    <button onClick={() => setRearrange(true)}
    type="button"
    className="inline-flex items-center gap-x-1 rounded-xl border border-white/20 bg-gradient-to-br from-indigo-900/90 to-indigo-700/90 px-2 py-1 sm:px-3.5 sm:py-2.5 text-xs sm:text-sm font-semibold text-white shadow-lg shadow-indigo-950/40 hover:from-indigo-800/95 hover:to-indigo-600/95"
  >
    <ArrowsRightLeftIcon aria-hidden="true" className="-ml-0.5 size-4 sm:size-5" />
    Rearrange
  </button>
  )}

<div className="w-full bg-white/70 border border-indigo-200/70 rounded-full h-4 sm:h-6 mb-2 sm:mb-4 overflow-hidden relative">
      <div className="flex h-full">
        {/* Completed segment */}
        <div 
          className="bg-gradient-to-r from-emerald-500 to-emerald-400 transition-all duration-300 ease-out" 
          style={{ width: `${completedPercent}%` }}
        />
        {/* Incomplete segment */}
        <div 
          className="bg-gradient-to-r from-rose-500 to-red-400 transition-all duration-300 ease-out" 
          style={{ width: `${incompletePercent}%` }}
        />
      </div>
      
      {/* Combined text overlay */}
      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-white text-xs font-medium">
        {totalTasks === 0 ? (
          <span>No tasks</span>
        ) : (
          <span className="text-[10px] sm:text-xs whitespace-nowrap">
            {completedTasks} Done • {incompleteTasks} Todo
            <span className="text-gray-300 mx-1 hidden sm:inline">|</span>
            <span className="hidden sm:inline">{totalTasks} Total</span>
          </span>
        )}
      </div>
    </div>
      
    </div>
</div>
</div>







{tasks.length === 0 && (
  <div className="p-4 rounded-2xl border border-red-200/60 bg-gradient-to-r from-rose-600/90 to-red-500/90 text-white shadow-lg shadow-rose-900/30 flex justify-between">
        <ExclamationCircleIcon className="h-12 w-12" aria-hidden="true" />
        <p className="text-lg text-white-600 mt-4">
          No tasks scheduled for today.
        </p>
        
        
      </div>
      )}





      
        {/* Task List */}
        <div className="flex-auto overflow-auto bg-transparent p-4">
  {/* Main Content Area */}
  {!bottleneck ? (
      <div className="overflow-auto bg-transparent p-4 transition-all duration-300 w-full">

    {/* Wrap the content to apply the zoom without affecting layout */}
    <div
      className="transition-all duration-300"
      style={{ transform: `scale(${zoom})`, transformOrigin: "top left" }}
    >
      
    <ul className="space-y-4">
  {Object.entries(groupedTasks).map(([startTime, tasksAtTime]) => (
     <li key={startTime} className="mb-4 sm:mb-6">
       {/* Time label */}
       <div className="mb-2 sticky top-0 z-10 bg-white/75 backdrop-blur-xl border border-indigo-200/70 p-2 rounded-xl shadow-sm">
         <h3 className="text-xs sm:text-sm font-medium text-indigo-900">Tasks at {startTime}</h3>
       </div>
       
       {/* Tasks grid - responsive layout */}
       <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-4">
         {tasksAtTime.map((task) => {
         const taskStartTime = parse(task.StartTime, "HH:mm:ss", new Date());
         const taskEndTime = addHours(taskStartTime, 1);
         let cardColor = "bg-gradient-to-br from-indigo-900/85 to-indigo-700/80";
         const isBreak = task.Break === 1;
         const completed = task.Completed === 1;
         const isIssue = task.Issue !== null;
         const helpReceived = task.HelpSuggestions !== null;
       
 
         
 
 
         // Set status icon: if not completed, show an X icon; if completed, show a check icon.
         let statusIcon = completed ? (
           <CheckCircleIcon className="h-6 w-6 text-white" aria-hidden="true" />
         ) : (
           <XCircleIcon className="h-6 w-6 text-white" aria-hidden="true" />
         );
 
 let issueIcon = isIssue ? (
   <div className="flex items-center space-x-2">
           <ExclamationCircleIcon className="h-6 w-6 text-white animate-pulse" aria-hidden="true" />
           {helpReceived ? (
             <span className="text-xs bg-white p-2 text-emerald-500 rounded-full px-2 py-1">
               Help Received!
             </span>
           ) : (
           <span className="text-xs bg-white p-2 text-red-500 rounded-full px-2 py-1">
             Need Help!
           </span>
           )}
         </div>
         ) : null;
 
 
         let button = null;
 
         // If the task has passed its end time, mark it as overdue.
         if (isBefore(taskEndTime, currentTime)) {
           cardColor = "bg-gradient-to-br from-rose-600/85 to-red-500/80";
           // If the task is completed, override with the check icon.
           if (completed) {
             statusIcon = (
 <button onClick={() => setOpenOutcomes(task.Outcome)}>
               <CheckCircleIcon className="h-12 w-12 text-emerald-300 animate-bounce" aria-hidden="true" />
                 </button>            );
           }
         } else if (
           isEqual(taskStartTime, currentTime) ||
           (isBefore(taskStartTime, currentTime) && isAfter(taskEndTime, currentTime))
         ) {
           cardColor = "bg-gradient-to-br from-emerald-600/85 to-emerald-500/80";
           statusIcon = (
             <UserCircleIcon className="h-6 w-6 text-white" aria-hidden="true" />
           );
           
          
         }
 
         const isExpanded = expandedCard === task.TaskID;
         
         // Function to toggle expansion
         const toggleExpand = () => {
           setExpandedCard(isExpanded ? null : task.TaskID);
         };
 
         // Calculate break proportions for split color layout
         let breakPercentage = 0;
         let workPercentage = 100;
         if (isBreak && task.BreakDuration) {
           breakPercentage = Math.min((task.BreakDuration / 60) * 100, 100);
           workPercentage = 100 - breakPercentage;
         }
         
         return (
           <div
             key={task.TaskID}
             onClick={toggleExpand}
             className={classNames(
               isBreak ? "" : cardColor,
               "flex border border-white/25 backdrop-blur-xl flex-col w-full rounded-2xl shadow-lg shadow-indigo-950/20 text-white transition-all duration-300 relative overflow-hidden",
               "cursor-pointer hover:shadow-lg",
               isExpanded ? "md:p-4" : "p-2 md:p-4"
             )}
             style={isBreak ? {
          background: `linear-gradient(to bottom, #a78bfa 0%, #a78bfa ${breakPercentage}%, #a78bfa ${breakPercentage}%, #6d28d9 100%)`
             } : {}}
           >
             {/* Break time indicator for break tasks */}
             {isBreak && task.BreakStart && task.BreakEnd && (
               <div 
                 className="absolute top-2 left-2 text-xs font-semibold text-indigo-900 bg-white/80 border border-indigo-200/70 px-2 py-1 rounded-xl z-10"
                 style={{ width: '100%', minWidth: '60px' }}
               >
                 {task.BreakStart} - {task.BreakEnd} ( Break )
               </div>
             )}
             {/* Mobile simplified card (when not expanded) */}
             <div className={`md:hidden ${isExpanded ? 'hidden' : 'block'} ${isBreak ? 'mt-8' : ''}`}>
               <div className="flex items-center justify-between">
                 <div className="flex items-center space-x-2">
                   <div className="w-8 h-8 flex items-center justify-center rounded-full bg-white/85 text-indigo-900 font-semibold text-sm">
                     {task.Step}
                   </div>
                   <div>
                     <p className="font-bold text-sm truncate max-w-[150px]">{task.Task}</p>
                     <p className="text-xs opacity-90">{task.Name}</p>
                   </div>
                 </div>
                 <div className="flex items-center">
                   {statusIcon}
                   <ChevronDoubleRightIcon className="w-5 h-5 ml-1 text-white" />
                 </div>
               </div>
             </div>
             
             {/* Full card content - always shown on desktop, shown on mobile only when expanded */}
             <div className={`${isExpanded ? 'block' : 'hidden md:block'} ${isBreak ? 'mt-8 md:mt-0' : ''} p-4`}>
               <div className="flex items-center justify-between">
                 <div className="w-12 h-12 flex items-center justify-center rounded-full bg-white text-indigo-600 font-semibold">
                   {task.Step}
                 </div>
                 {task.ProjectName !== null && (
                   <p className="text-xs p-2 bg-white/80 border border-indigo-200/70 text-indigo-900 inline-block font-bebas rounded-xl">{task.ProjectName}</p>
                 )}
                 <button 
                   onClick={(e) => { 
                     e.stopPropagation(); 
                     handleTaskSelect(task.id);
                   }}
                   data-component="project-daily-tasks-card"
                   data-feature="open_task_details"
                   data-task-id={task.TaskID}
                 >
                   <ChevronDoubleRightIcon className="w-10 h-10 md:w-16 md:h-16 text-white" />
                 </button>
                 <button 
                   onClick={(e) => {
                     e.stopPropagation();
                     duplicateTask(task.TaskID);
                   }}
                   data-component="project-daily-tasks-card"
                   data-feature="duplicate_task_post"
                   data-task-id={task.TaskID}
                 > 
                   <Square2StackIcon className="h-5 w-5 md:h-6 md:w-6 text-white" aria-hidden="true" />
                 </button> 
                 <button 
                   onClick={(e) => {
                     e.stopPropagation();
                     handleDeleteTask(task.TaskID);
                   }}
                   data-component="project-daily-tasks-card"
                   data-feature="delete_task_delete"
                   data-task-id={task.TaskID}
                 >
                   <XCircleIcon className="h-5 w-5 md:h-6 md:w-6 text-red-100" aria-hidden="true" />
                 </button>
               </div>
               
               <p className="text-sm font-semibold mt-2">
                 {task.Name} - {task.Role}
               </p>
               <p className="text-xs">{task.ProcessName}</p>
               <div className="flex items-center space-x-2 mt-2 flex-wrap">
                 {statusIcon} {issueIcon}
                 {task.Priority === "High" && (
                   <span className="text-xs bg-red-500 text-white rounded-full px-2 py-1">
                     High!
                   </span>
                 )}
                 {task.Priority === "Medium" && (
                   <span className="text-xs bg-yellow-500 text-white rounded-full px-2 py-1">
                     Medium
                   </span>
                 )}
                 {task.Priority === "Low" && (
                   <span className="text-xs bg-green-500 text-white rounded-full px-2 py-1">
                     Low
                   </span>
                 )}
                 <button
                   onClick={(e) => {
                     e.stopPropagation();
                     handlePriorityChange(task.TaskID, "decrease");
                   }}
                   className="rounded-xl border border-white/20 bg-white/20 px-2 py-1 text-xs font-semibold text-white shadow-sm hover:bg-white/30"
                   data-component="project-daily-tasks-card"
                   data-feature="priority_decrease_put"
                   data-task-id={task.TaskID}
                 >
                   <MinusCircleIcon className="h-5 w-5 text-white" aria-hidden="true" />
                 </button>
                 <button
                   onClick={(e) => {
                     e.stopPropagation();
                     handlePriorityChange(task.TaskID, "increase");
                   }}
                   className="rounded-xl border border-white/20 bg-white/10 px-2 py-1 text-xs font-semibold text-white shadow-sm hover:bg-white/30"
                    data-component="project-daily-tasks-card"
                    data-feature="priority_increase_put"
                    data-task-id={task.TaskID}
                 >
                   <PlusCircleIcon className="h-5 w-5 text-white" aria-hidden="true" />
                 </button>
               </div>
               <p className="font-semibold mt-2">{task.Task}</p>
               {task.Description && (
                 <p className="text-sm mt-2">{task.Description}</p>
               )}
               <p className="text-sm mt-2">
                 <time>{format(taskStartTime, "HH:mm")}</time> -{" "}
                 <time>{format(taskEndTime, "HH:mm")}</time>
               </p>
               
               <div className="flex items-center space-x-2 mt-2">
               
               {sendMessage === null &&  (
                 <button 
                   onClick={(e) => {
                     e.stopPropagation();
                     setSendMessage(task.TaskID);
                   }} 
                   className="rounded-xl mr-2 sm:mr-3 border border-white/20 bg-white/80 px-2 py-1 sm:px-3 sm:py-2 text-xs sm:text-sm font-semibold text-indigo-900 shadow-sm hover:bg-white"
                   data-component="project-daily-tasks-card"
                   data-feature="open_task_messaging"
                   data-task-id={task.TaskID}
                 >
                   <ChatBubbleBottomCenterIcon className="h-3 w-3" aria-hidden="true" />
                 </button>
                 )}
                 {sendMessage === task.TaskID && (
                   <ChatBubbleBottomCenterIcon className="h-6 w-6 sm:h-8 sm:w-8 animate-bounce" aria-hidden="true" />
                 )}
 
                {/* Material usage button - only show for External projects */}
                {projectType === 'External' && getBoughtMaterials().length > 0 && (
                  <>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowMaterialDropdown(showMaterialDropdown === task.TaskID ? null : task.TaskID);
                        setSelectedMaterialForUse(null);
                        setMaterialQuantity(1);
                      }}
                      className="rounded-xl border border-white/20 bg-gradient-to-br from-amber-600/90 to-orange-500/90 px-2 py-1 sm:px-3 sm:py-2 text-xs sm:text-sm font-semibold text-white shadow-md hover:from-amber-500/95 hover:to-orange-400/95"
                      data-component="project-daily-tasks-card"
                      data-feature="open_material_dropdown"
                      data-task-id={task.TaskID}
                    >
                      <CubeIcon className="h-4 w-4" aria-hidden="true" />
                    </button>

                    {/* Material dropdown modal */}
                    {showMaterialDropdown === task.TaskID && (
                      <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4 z-[100]" onClick={(e) => {
                        e.stopPropagation();
                        setShowMaterialDropdown(null);
                        setSelectedMaterialForUse(null);
                      }}>
                        <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] sm:max-h-[85vh] overflow-hidden mx-2 sm:mx-4 flex flex-col" onClick={(e) => e.stopPropagation()}>
                          <div className="bg-gradient-to-r from-amber-500 to-orange-600 p-3 sm:p-4 rounded-t-xl flex-shrink-0">
                            <div className="flex justify-between items-center">
                              <h3 className="text-sm sm:text-base md:text-lg font-semibold text-white">Use Material</h3>
                              <button 
                                className="text-white hover:bg-white hover:bg-opacity-20 rounded-full h-7 w-7 sm:h-8 sm:w-8 flex items-center justify-center transition-colors text-base sm:text-lg"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setShowMaterialDropdown(null);
                                  setSelectedMaterialForUse(null);
                                }}
                              >
                                ✕
                              </button>
                            </div>
                          </div>
                          
                          <div className="p-3 sm:p-4 md:p-6 overflow-y-auto flex-1">
                            {/* Material selection */}
                            {!selectedMaterialForUse && (
                              <div className="space-y-2">
                                <p className="text-xs sm:text-sm text-gray-600 mb-2 sm:mb-3">Select a material to use for this task</p>
                                {getBoughtMaterials().map((material, idx) => {
                                  const actualIndex = projectMaterials.indexOf(material);
                                  return (
                                    <button
                                      key={idx}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedMaterialForUse({ material, index: actualIndex });
                                      }}
                                      className="w-full text-left px-3 py-2 sm:py-3 hover:bg-amber-50 active:bg-amber-100 rounded-md border border-gray-200 transition-colors"
                                    >
                                      <div className="font-medium text-sm sm:text-base text-gray-900">{material.name}</div>
                                      <div className="text-xs text-gray-600 mt-1">
                                        Available: {material.quantity} {material.unit}
                                      </div>
                                      <div className="text-xs text-gray-500">{material.category}</div>
                                    </button>
                                  );
                                })}
                              </div>
                            )}

                            {/* Quantity selection */}
                            {selectedMaterialForUse && (
                              <div className="space-y-3 sm:space-y-4">
                                <div className="p-3 bg-gray-50 rounded-lg">
                                  <div className="font-medium text-sm sm:text-base text-gray-900">{selectedMaterialForUse.material.name}</div>
                                  <div className="text-xs text-gray-600 mt-1">
                                    Available: {selectedMaterialForUse.material.quantity} {selectedMaterialForUse.material.unit}
                                  </div>
                                  <div className="text-xs text-gray-500">{selectedMaterialForUse.material.category}</div>
                                </div>
                                
                                <div>
                                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                                    Quantity to Use
                                  </label>
                                  <input
                                    type="number"
                                    inputMode="decimal"
                                    min="0.01"
                                    max={selectedMaterialForUse.material.quantity}
                                    step="0.01"
                                    value={materialQuantity}
                                    onChange={(e) => {
                                      const val = e.target.value;
                                      if (val === '' || val === '0') {
                                        setMaterialQuantity('');
                                      } else {
                                        const numVal = parseFloat(val);
                                        if (!isNaN(numVal) && numVal > 0) {
                                          setMaterialQuantity(Math.min(numVal, selectedMaterialForUse.material.quantity));
                                        }
                                      }
                                    }}
                                    onBlur={(e) => {
                                      if (e.target.value === '' || parseFloat(e.target.value) <= 0) {
                                        setMaterialQuantity(1);
                                      }
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                    className="w-full px-3 py-2 sm:py-2.5 text-sm sm:text-base text-gray-900 border border-gray-300 rounded-md focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                                    placeholder="Enter quantity"
                                  />
                                  <div className="text-xs text-gray-500 mt-1">
                                    Max: {selectedMaterialForUse.material.quantity} {selectedMaterialForUse.material.unit}
                                  </div>
                                </div>

                                <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 pt-2">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const qty = materialQuantity === '' ? 1 : (typeof materialQuantity === 'number' ? materialQuantity : parseFloat(materialQuantity));
                                      if (qty > 0 && qty <= selectedMaterialForUse.material.quantity) {
                                        handleUseMaterial(
                                          task.TaskID,
                                          task.Task,
                                          selectedMaterialForUse.index,
                                          qty
                                        );
                                      } else {
                                        alert(`Please enter a valid quantity between 0.01 and ${selectedMaterialForUse.material.quantity}`);
                                      }
                                    }}
                                    className="flex-1 px-4 py-2.5 sm:py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white text-sm sm:text-base font-medium rounded-md hover:from-green-700 hover:to-emerald-700 active:from-green-800 active:to-emerald-800 transition-colors shadow-md"
                                  >
                                    Use Material
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setSelectedMaterialForUse(null);
                                      setMaterialQuantity(1);
                                    }}
                                    className="flex-1 px-4 py-2.5 sm:py-2 bg-gray-400 text-white text-sm sm:text-base font-medium rounded-md hover:bg-gray-500 active:bg-gray-600 transition-colors shadow-md"
                                  >
                                    Back
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
                
                 <button
                   onClick={(e) => {
                     e.stopPropagation();
                     handleTasksChange(task.TaskID);
                   }}
                   className="rounded-xl border border-white/20 bg-white/80 px-2 py-1 sm:px-3 sm:py-2 text-xs sm:text-sm font-semibold text-emerald-700 shadow-sm hover:bg-white"
                 >
                   <ArrowsRightLeftIcon className="h-3 w-3" aria-hidden="true" />
                 </button>
               </div>
               {button}
             </div>
             
           </div>
         );
       })}
         </div>
       </li>
     ))}
</ul>


    </div>
  </div>
  ) : (
    <div className="w-full overflow-auto">
<Bottlenecks tasks={tasks} onTriggerParentEffect={fetchData}
 />
    </div>
  )}
 
  {rearrange && (
    <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[90vh] sm:max-h-[85vh] overflow-y-auto mx-2 sm:mx-4">
        <div className="bg-gradient-to-r from-purple-500 to-indigo-600 p-3 sm:p-4 rounded-t-xl">
          <div className="flex justify-between items-center">
            <h3 className="text-base sm:text-lg font-semibold text-white">Rearrange Tasks - Select New Times</h3>
            <button 
              className="text-white hover:bg-white hover:bg-opacity-20 rounded-full h-6 w-6 sm:h-7 sm:w-7 flex items-center justify-center transition-colors text-sm sm:text-base"
              onClick={() => setRearrange(false)}
            >
              ✕
            </button>
          </div>
        </div>
        <div className="p-3 sm:p-6">
          <RearrangeTasksDropdown 
            groupedTasks={groupedTasks} 
            onTriggerParentEffect={fetchData}
            onClose={() => setRearrange(false)}
          />
        </div>
      </div>
    </div>
  )}




  {/* SubstepViewer Overlay */}
  {selectedTaskId && (
    <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] sm:max-h-[85vh] overflow-y-auto mx-2 sm:mx-4">
        <div className="bg-gradient-to-r from-indigo-500 to-blue-600 p-3 sm:p-4 rounded-t-xl">
          <div className="flex justify-between items-center">
            <h3 className="text-base sm:text-lg font-semibold text-white">Task Details</h3>
            <button 
              className="text-white hover:bg-white hover:bg-opacity-20 rounded-full h-6 w-6 sm:h-7 sm:w-7 flex items-center justify-center transition-colors text-sm sm:text-base"
              onClick={() => setSelectedTaskId(null)}
            >
              ✕
            </button>
          </div>
        </div>
        <div className="p-3 sm:p-6">
          <SubstepViewer taskData={selectedTaskId} />
        </div>
      </div>
    </div>
  )}

  {/* Messages Overlay */}
  {sendMessage && (
    <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] sm:max-h-[85vh] overflow-y-auto mx-2 sm:mx-4">
        <div className="bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 p-3 sm:p-4 rounded-t-xl">
          <div className="flex justify-between items-center">
            <h3 className="text-base sm:text-lg font-semibold text-white">Send Message</h3>
            <button 
              className="text-white hover:bg-white hover:bg-opacity-20 rounded-full h-6 w-6 sm:h-7 sm:w-7 flex items-center justify-center transition-colors text-sm sm:text-base"
              onClick={() => setSendMessage(null)}
            >
              ✕
            </button>
          </div>
        </div>
        <div className="p-3 sm:p-6">
          <div className="relative rounded-2xl sm:rounded-3xl p-1 bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500 shadow-xl">
            <div className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-2xl sm:rounded-3xl p-4 sm:p-6">
              <div className="relative">
                <textarea
                  onChange={(e => setInstructionText(e.target.value))}
                  value={instructionText}
                  className="w-full h-24 sm:h-28 resize-none rounded-xl border border-white/20 bg-white/10 p-3 sm:p-4 pr-10 sm:pr-12 text-sm text-white placeholder-white/70 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-white/30 focus:border-transparent"
                  placeholder="Type your message..."
                />

                <button
                  onClick={() => handleSendMessage(sendMessage)}
                  className="absolute bottom-2 sm:bottom-3 right-2 sm:right-3 bg-white/20 hover:bg-white/30 p-1.5 sm:p-2 rounded-full shadow-md transition backdrop-blur-md"
                  data-component="project-daily-tasks-messages"
                  data-time-saved="2"
                  data-feature="send_task_message_post"
                  data-task-id={sendMessage}
                >
                  <PaperAirplaneIcon className="h-4 w-4 sm:h-5 sm:w-5 text-white animate-pulse" />
                </button>
              </div>
            </div>
            {taskMessages.length > 0 && (
              <div className="mt-3 sm:mt-4 p-3 sm:p-4 bg-white/20 rounded-2xl sm:rounded-3xl backdrop-blur-md">
                <ul className="space-y-2">
                  {taskMessages.map((message, index) => (
                    <li key={`message-${message.id || index}-${message.CreatedAt}`} className="text-xs sm:text-sm text-white">
                      <span className="font-semibold">{message.from}:</span> - {message.CreatedAt}
                      {message.content}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )}

  {/* Outcomes Overlay */}
  {openOutcomes && (
    <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] sm:max-h-[85vh] overflow-y-auto mx-2 sm:mx-4">
        <div className="bg-gradient-to-r from-green-500 to-emerald-600 p-3 sm:p-4 rounded-t-xl">
          <div className="flex justify-between items-center">
            <h3 className="text-base sm:text-lg font-semibold text-white">Task Outcome</h3>
            <button 
              className="text-white hover:bg-white hover:bg-opacity-20 rounded-full h-6 w-6 sm:h-7 sm:w-7 flex items-center justify-center transition-colors text-sm sm:text-base"
              onClick={() => setOpenOutcomes(null)}
            >
              ✕
            </button>
          </div>
        </div>
        <div className="p-3 sm:p-6">
          <div className="flex flex-col gap-2">
            <h2 className="text-base sm:text-lg font-semibold text-gray-800">Outcome for Task</h2>
            <p className="text-sm text-gray-600">{openOutcomes}</p>
          </div>
        </div>
      </div>
    </div>
  )}
</div>


{newTaskTrue && (
  <div className="fixed inset-0 bg-gray-900 bg-opacity-90 flex items-center justify-center p-4 z-[50]">
    <div className="bg-white p-6 rounded-lg shadow-lg w-80 space-y-4">
      
      {/* 📌 First Dropdown: Select Process */}
      <div>
        <label className="block text-gray-700 font-semibold mb-1">Select Process</label>
        <select
          className="w-full rounded-lg border border-gray-300 p-2 text-black"
          onChange={(e) => {
            const selected = changeTask.find((proc) => proc.id === parseInt(e.target.value));
            setSelectProcess(selected);
            setSelectedTask(""); // Reset task dropdown when a new process is selected
          }}
          value={selectedProcess?.id || ""}
        >
          <option value="" disabled>
            Choose a process
          </option>
          {changeTask.map((process) => (
            <option key={process.id} value={process.id}>
              {process.Name}
            </option>
          ))}
        </select>
      </div>

      {/* 📌 Second Dropdown: Select Task (Appears When Process is Selected) */}
      {selectedProcess && (
        <div>
          <label className="block text-gray-700 font-semibold mb-1">Select Task</label>
          <select
  className="w-full rounded-lg border border-gray-300 p-2 text-black"
  onChange={(e) => {
    const selected = selectedProcess.tasks.find(task => task.Task === e.target.value);
    setSelectedTask({
      task: selected?.Task || "",
      role: selected?.Role || "",
      id: selected?.id || "",
      step : selected?.Step || ""
    });
  }}
  value={selectedTask?.task || ""}
>
  <option value="" disabled>
    Choose a task
  </option>
  {selectedProcess.tasks.map((task) => (
    <option key={task.id} value={task.Task}>
      {task.Step}: {task.Task} - {task.Role}
    </option>
  ))}
</select>

        </div>
      )}

      {/* 📌 Task Description Input */}
      {selectedTask && (
        <div>
          <label className="block text-gray-700 font-semibold mb-1">Description</label>
          <textarea
            className="w-full rounded-lg border border-gray-300 p-2 text-black resize-none"
            onChange={(e) => setInstructionText(e.target.value)}
            value={instructionText}
            rows="3"
          />

          <select value={newRole} onChange={(e) => {

            const selected = roles.find((role) => role.id === parseInt(e.target.value));

            setNewRole(
              {id: selected.id, Name: selected.Name}
            );

          }

          } className="w-full rounded-md border border-gray-300 p-2 text-black">
            <option value="all">Staff </option>
            {roles.map((role, index) => (
              <option key={`role-${role.id || index}`} value={role.id}>
                {role.Name}
              </option>
            ))}

          </select>
          <input 
          type="time"
           value={time} 
           onChange={(e) => handleSetTime(e.target.value)}
          className="w-full rounded-md border border-gray-300 p-2 text-black" placeholder="Staff Name" />
{error && <p className="text-red-500 text-xs">{error}</p>}
          {/* ✅ Save Button */}
          <button
            onClick={() => handleNewTask()}
            className="w-full mt-3 rounded-md bg-emerald-500 px-4 py-2 text-white font-semibold shadow-md hover:bg-emerald-600 transition duration-200"
            data-component="project-daily-tasks-new-task"
            data-time-saved="2"
            data-feature="create_task_post"
            data-role-name={typeof newRole === "object" ? newRole?.Name : newRole}
          >
            Save Task
          </button>
          <button onClick={() => setNewTaskTrue(false)|| setSelectProcess("")} className="w-full mt-3 rounded-md bg-red-500 px-4 py-2 text-white font-semibold shadow-md hover:bg-red-600 transition duration-200">
            Cancel
          </button>
        </div>
      )}

      {/* 📌 Selected Task Display */}
      {selectedTask && (
        <div className="mt-4 flex items-center gap-4 bg-gray-100 px-6 py-3 rounded-lg shadow-md">
        <span className="font-semibold text-xs text-blue-600">{selectedTask.task}</span>
        <span className="font-semibold  text-xs text-blue-600">{selectedTask.role}</span>
        <span className="font-semibold  text-xs text-blue-600">{newRole.Name}</span>
      </div>
      
      )}
    </div>
  </div>
)}

{/* Change Task Modal */}
{showChangeTaskModal && (
  <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4 z-50">
    <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] sm:max-h-[85vh] overflow-y-auto mx-2 sm:mx-4">
      <div className="bg-gradient-to-r from-emerald-500 to-teal-600 p-3 sm:p-4 rounded-t-xl">
        <div className="flex justify-between items-center">
          <h3 className="text-base sm:text-lg font-semibold text-white">Change Task</h3>
          <button 
            className="text-white hover:bg-white hover:bg-opacity-20 rounded-full h-6 w-6 sm:h-7 sm:w-7 flex items-center justify-center transition-colors text-sm sm:text-base"
            onClick={() => {
              setShowChangeTaskModal(false);
              setChangeTaskId(null);
              setSelectProcess(null);
              setSelectedTask("");
            }}
          >
            ✕
          </button>
        </div>
      </div>
      <div className="p-3 sm:p-6 space-y-3 sm:space-y-4">
        {/* First Dropdown: Select Process */}
        <div>
          <label className="block text-gray-700 font-semibold mb-1 sm:mb-2 text-sm sm:text-base">Select Process</label>
          <select
            className="w-full rounded-lg border border-gray-300 p-2 sm:p-3 text-sm sm:text-base text-black focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            onChange={(e) => {
              const selected = changeTask.find((proc) => proc.id === parseInt(e.target.value));
              setSelectProcess(selected);
              setSelectedTask("");
            }}
            value={selectedProcess?.id || ""}
          >
            <option value="" disabled>
              Choose a process
            </option>
            {changeTask && changeTask.map((process) => (
              <option key={process.id} value={process.id}>
                {process.Name}
              </option>
            ))}
          </select>
        </div>

        {/* Second Dropdown: Select Task */}
        {selectedProcess && (
          <div>
            <label className="block text-gray-700 font-semibold mb-1 sm:mb-2 text-sm sm:text-base">Select Task</label>
            <select
              className="w-full rounded-lg border border-gray-300 p-2 sm:p-3 text-sm sm:text-base text-black focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              onChange={(e) => {
                const selected = selectedProcess.tasks.find(task => task.Task === e.target.value);
                setSelectedTask({
                  task: selected?.Task || "",
                  role: selected?.Role || "",
                  id: selected?.id || "",
                  step: selected?.Step || ""
                });
              }}
              value={selectedTask?.task || ""}
            >
              <option value="" disabled>
                Choose a task
              </option>
              {selectedProcess.tasks.map((task) => (
                <option key={task.id} value={task.Task}>
                  {task.Step}: {task.Task} - {task.Role}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Selected Task Display */}
        {selectedTask && (
          <>
            <div className="mt-3 sm:mt-4 p-3 sm:p-4 bg-gray-100 rounded-lg">
              <div className="space-y-2">
                <div className="text-xs sm:text-sm text-gray-700">
                  <span className="font-semibold">Task:</span> {selectedTask.task}
                </div>
                <div className="text-xs sm:text-sm text-gray-700">
                  <span className="font-semibold">Role:</span> {selectedTask.role}
                </div>
                <div className="text-xs sm:text-sm text-gray-700">
                  <span className="font-semibold">Step:</span> {selectedTask.step}
                </div>
              </div>
            </div>
            
            <div className="mt-3 sm:mt-4">
              <label className="block text-gray-700 font-semibold mb-1 sm:mb-2 text-sm sm:text-base">Description</label>
              <textarea
                className="w-full rounded-lg border border-gray-300 p-2 sm:p-3 text-sm sm:text-base text-black resize-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                onChange={(e) => setInstructionText(e.target.value)}
                value={instructionText}
                rows="3"
                placeholder="Enter task description..."
              />
            </div>
          </>
        )}

        {/* Action buttons */}
        <div className="flex gap-2 sm:gap-3 pt-3 sm:pt-4">
          <button
            onClick={() => {
              if (selectedTask && changeTaskId) {
                handleSaveTask(changeTaskId);
                setShowChangeTaskModal(false);
                setChangeTaskId(null);
                setSelectProcess(null);
                setSelectedTask("");
              }
            }}
            disabled={!selectedTask}
            className="flex-1 px-3 sm:px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-600 text-white text-xs sm:text-sm font-medium rounded-md hover:from-emerald-600 hover:to-teal-700 transition-colors shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
            data-component="project-daily-tasks-change"
            data-time-saved="2"
            data-feature="change_task_put"
            data-task-id={changeTaskId}
          >
            Change Task
          </button>
          <button
            onClick={() => {
              setShowChangeTaskModal(false);
              setChangeTaskId(null);
              setSelectProcess(null);
              setSelectedTask("");
            }}
            className="flex-1 px-3 sm:px-4 py-2 bg-gray-400 text-white text-xs sm:text-sm font-medium rounded-md hover:bg-gray-500 transition-colors shadow-md"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  </div>
)}
      {openOutcome && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center">
          <div className="bg-white p-4 rounded-md shadow-lg w-96">
            <h2 className="text-lg font-semibold text-gray-800">
              Outcome for Task
            </h2>
            <textarea
              value={instructionText}
              onChange={(e) => setInstructionText(e.target.value)}
              className="w-full h-32 p-2 border border-gray-300 rounded-md mt-2"
              placeholder="Enter the outcome for this task..."
            />
            <div className="flex items-center justify-end mt-4">
              <button
                onClick={() => setOpenOutcome(null)}
                className="bg-gray-200 text-gray-800 px-3 py-2 rounded-md shadow-sm hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={() => handleSaveOutcome(openOutcome)}
                className="bg-indigo-600 text-white px-3 py-2 rounded-md shadow-sm hover:bg-indigo-700 ml-2"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Break Form Modal */}
      {showBreakForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[85vh] overflow-y-auto">
            {/* Header */}
            <div className="bg-gradient-to-r from-yellow-500 to-amber-600 p-4 rounded-t-xl">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-white">Add Break</h3>
                <button 
                  className="text-white hover:bg-white hover:bg-opacity-20 rounded-full h-7 w-7 flex items-center justify-center transition-colors"
                  onClick={handleBreakCancel}
                >
                  ✕
                </button>
              </div>
              {breakLength > 0 && (
                <div className="mt-2 text-sm text-white">
                  Your entitled break: {breakLength} minutes
                </div>
              )}
            </div>
            
            {/* Form Content */}
            <div className="p-6">
              <div className="text-sm font-medium text-gray-700 mb-4">Break Time Details</div>
              
              {/* Staff member selection */}
              <div className="mb-4">
                <label className="block text-sm text-gray-700 mb-2">Staff Member</label>
                <select
                  value={selectedBreakStaff}
                  onChange={(e) => setSelectedBreakStaff(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                >
                  <option value="">Select Staff Member</option>
                  {staff.map((member) => (
                    <option key={`staff-${member.id}`} value={member.id}>
                      {member.Name} - {member.Role}
                    </option>
                  ))}
                </select>
              </div>
              
              {/* Time inputs */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm text-gray-700 mb-2">Start Time</label>
                  <input
                    type="time"
                    value={breakStartTime}
                    onChange={(e) => setBreakStartTime(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-2">End Time</label>
                  <input
                    type="time"
                    value={breakEndTime}
                    onChange={(e) => setBreakEndTime(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                  />
                </div>
              </div>
              
              {/* Additional task selection (shown only if break length < 60 minutes) */}
              {breakLength > 0 && breakLength < 60 && (
                <div className="mb-4">
                  <label className="block text-sm text-gray-700 mb-2">
                    Additional Task (Optional)
                    <span className="text-xs text-gray-500 ml-1">- For breaks under 60 minutes</span>
                  </label>
                  <select
                    value={selectedBreakTask}
                    onChange={handleAdditionalTaskChange}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                  >
                    <option value="">Select Additional Task</option>
                    {availableTasks.map((task, index) => (
                      <option key={`task-${task.id}-${task.name}-${task.step}-${index}`} value={task.id}>
                        {task.name}
                        {task.step && ` (Step ${task.step})`}
                        {task.processName && ` - ${task.processName}`}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    You can continue working on a task during your break if it's under 60 minutes.
                  </p>
                </div>
              )}
              
              {/* Action buttons */}
              <div className="flex gap-3">
                <button
                  onClick={handleBreakSubmit}
                  className="flex-1 px-4 py-2 bg-gradient-to-r from-yellow-500 to-amber-600 text-white text-sm font-medium rounded-md hover:from-yellow-600 hover:to-amber-700 transition-colors shadow-md"
                  data-component="project-daily-tasks-breaks"
                  data-feature="save_break_post"
                  data-staff-id={selectedBreakStaff || undefined}
                >
                  Add Break
                </button>
                <button
                  onClick={handleBreakCancel}
                  className="flex-1 px-4 py-2 bg-gray-400 text-white text-sm font-medium rounded-md hover:bg-gray-500 transition-colors shadow-md"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}



    </>
  );
}

